import React, { useState, useEffect } from 'react'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from 'components/ui/dialog'
import { Trash2 } from 'lucide-react'

export interface DeleteQuarterModalProps {
	isOpen: boolean
	onClose: () => void
	onConfirmDelete: () => void
	quarterLabel: string
}

export const DeleteQuarterModal: React.FC<DeleteQuarterModalProps> = ({
	isOpen,
	onClose,
	onConfirmDelete,
	quarterLabel,
}) => {
	const [inputValue, setInputValue] = useState('')
	const [error, setError] = useState<string | null>(null)

	useEffect(() => {
		if (isOpen) {
			setInputValue('')
			setError(null)
		}
	}, [isOpen])

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setInputValue(e.target.value)
		setError(null)
	}

	const isMatch = inputValue.trim() === quarterLabel

	const handleConfirm = () => {
		if (!isMatch) {
			setError(`Please enter exactly "${quarterLabel}" to confirm deletion`)
			return
		}
		onConfirmDelete()
		onClose()
	}

	return (
		<Dialog open={isOpen} onClose={onClose}>
			<DialogContent className="max-w-md">
				<DialogHeader>
					<div className="flex items-center gap-3">
						<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-red-100 text-red-600">
							<Trash2 className="w-5 h-5" />
						</div>
						<div>
							<DialogTitle>Delete Quarter</DialogTitle>
							<DialogDescription>Permanently remove a quarter and its data</DialogDescription>
						</div>
					</div>
				</DialogHeader>

				<div className="px-5 py-4 space-y-4">
					{/* Warning */}
					<div className="bg-red-50 border border-red-200 rounded-md p-4">
						<div className="flex items-start">
							<svg
								className="w-6 h-6 text-red-600 mt-0.5 mr-3 flex-shrink-0"
								fill="currentColor"
								viewBox="0 0 20 20"
							>
								<path
									fillRule="evenodd"
									d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
									clipRule="evenodd"
								/>
							</svg>
							<div>
								<h3 className="text-sm font-semibold text-red-800 mb-1">Warning: Permanent Data Loss</h3>
								<p className="text-sm text-red-700">
									Deleting <span className="font-semibold">{quarterLabel}</span> will permanently remove all
									financial data for this quarter. This action cannot be undone.
								</p>
							</div>
						</div>
					</div>

					{/* Confirmation Input */}
					<div>
						<label htmlFor="quarter-confirm" className="block text-sm font-medium text-gray-700 mb-2">
							To confirm deletion, please type{' '}
							<span className="font-semibold text-gray-900">"{quarterLabel}"</span> below:
						</label>
						<Input
							id="quarter-confirm"
							type="text"
							value={inputValue}
							onChange={handleInputChange}
							placeholder={`Type ${quarterLabel} here`}
							autoComplete="off"
						/>
						{error && (
							<p className="mt-1 text-sm text-red-600">{error}</p>
						)}
					</div>

					{/* Match Indicator */}
					{inputValue && (
						<div
							className={`text-sm font-medium ${
								isMatch ? 'text-green-600' : 'text-gray-500'
							}`}
						>
							{isMatch ? '✓ Match confirmed' : '⨯ Does not match'}
						</div>
					)}
				</div>

				<DialogFooter>
					<Button variant="ghost" onClick={onClose}>
						Cancel
					</Button>
					<Button
						variant="danger"
						onClick={handleConfirm}
						disabled={!isMatch}
					>
						<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
							/>
						</svg>
						Remove Qtr
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
